import { classifyEscalationReason } from '@lume/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useClaimConversation } from '../hooks/use-claim-conversation';
import { useEscalationEvents } from '../hooks/use-escalation-events';
import { useTakeOver } from '../hooks/use-take-over';

import type { ConversationDelegation } from '../../../lib/types';
import type { EscalationDeclinedOrTimeoutPayload } from '../../../lib/websocket/events';
import type { EscalationEvent } from '../hooks/use-escalation-events';

/**
 * Fatia 11.1 / Items 280+281 — AiConductingBanner.
 *
 * Banner sticky no topo da Conversa em modo H_c (IA dirigindo autonomamente).
 * Substitui visualmente o Composer (que fica oculto/lockedHC) e indica ao
 * atendente que a IA está conduzindo, com botão "Retomar (Cmd+R)" para
 * transição H_c → H_a.
 *
 * Visual canonical conforme Brief 2A V2.3 §C.1 (matriz brief-2a-composer-header.md):
 *   - Bloco `composer--hc-locked`: ícone sparkles + texto + botão variante `btn--ai`
 *   - Atalho ⌘R (Cmd+R) escuta em window keydown enquanto banner ativo
 *
 * Em sucesso de takeOver: cache invalidate dispara re-render do ConversationView
 * com operationMode=H_a, banner some, Composer reaparece.
 *
 * Items 280+281 (T B10) — 4 sub-estados:
 *   - conducting: estado padrão (IA conduzindo)
 *   - awaiting-consent: IA sugeriu escalation, aguardando resposta do cliente
 *   - confirmed-handoff: cliente confirmou, auto-retorno em 3s
 *   - declined-or-timeout: cliente recusou ou timeout, com reason discriminado
 *
 * S7 PR1 (§4.2) — 5º sub-estado `delegating` (delegação por etapa, S5,
 * ADR-154): derivado do prop `delegation` (passthrough do wire, NÃO da
 * máquina de eventos de escalation acima) — sempre que
 * `delegation !== null` o banner mostra os objetivos delegados + countdown
 * do timeout de segurança + "Reassumir agora", INDEPENDENTE do que a
 * máquina de escalation interna diz (`delegating` tem prioridade — reflete
 * prop, então reage a live re-render via invalidação de query, ao contrário
 * dos outros 4 sub-estados que só mudam via WS `useEscalationEvents`).
 * `delegation` volta a `null` (janela fechada — completude/timeout/guard) →
 * o banner cai de volta pro sub-estado da máquina de eventos.
 */

type BannerSubState =
  | 'conducting'
  | 'awaiting-consent'
  | 'confirmed-handoff'
  | 'declined-or-timeout'
  // S7 PR2 (§5.1) — modo recepcionista (S4): a IA SEGURA a fila em H_c até o
  // claim (escalationState `awaiting_claim`). Botão "Assumir".
  | 'queue-held';

type VariantKey = 'neutral' | 'amber' | 'green' | 'warning';

interface SubStateConfig {
  variant: VariantKey;
  showRetakeBtn: boolean;
  /** null = persiste indefinidamente */
  ttlMs: number | null;
}

const SUBSTATE_CONFIG: Record<BannerSubState, SubStateConfig> = {
  conducting: { variant: 'neutral', showRetakeBtn: true, ttlMs: null },
  'awaiting-consent': { variant: 'amber', showRetakeBtn: true, ttlMs: null },
  'confirmed-handoff': { variant: 'green', showRetakeBtn: false, ttlMs: 3000 },
  'declined-or-timeout': { variant: 'warning', showRetakeBtn: true, ttlMs: null },
  'queue-held': { variant: 'amber', showRetakeBtn: true, ttlMs: null },
};

/* Tokens semânticos do DS (não paleta Tailwind crua) — fiel ao gabarito 2A
   §C.1 (.banner--ai-conducting) e dark-mode-safe. neutral=IA conduzindo,
   amber=aguardando consentimento (info), green=handoff confirmado (success),
   warning=recusado/timeout. */
const VARIANT_TOKENS: Record<VariantKey, { bg: string; border: string; fg: string }> = {
  neutral: { bg: 'var(--ai-bg)', border: 'var(--ai-border)', fg: 'var(--ai-fg)' },
  amber: { bg: 'var(--info-bg)', border: 'var(--info-border)', fg: 'var(--info-fg)' },
  green: { bg: 'var(--success-bg)', border: 'var(--success-border)', fg: 'var(--success-fg)' },
  warning: { bg: 'var(--warning-bg)', border: 'var(--warning-border)', fg: 'var(--warning-fg)' },
};

/**
 * S7 PR2 (§5.1) — copy do sub-estado `queue-held` derivada da CLASSE de postura
 * (`classifyEscalationReason` de `@lume/shared` — fonte autoritativa única, o FE
 * NÃO re-hardcodeia a matriz de motivos). `null`/motivo desconhecido ⇒ classe 2
 * (mesmo default conservador do backend).
 */
function queueHeldSecondaryByClass(escalationReason: string | null): string {
  switch (classifyEscalationReason(escalationReason)) {
    case 1:
      // Instrumento falhou / assunto fora de escopo — a IA conduz útil na espera.
      return 'Transferência solicitada — a IA mantém o cliente enquanto você não assume.';
    case 3:
      // Proteção/segurança — a IA registrou e não conduz mais.
      return 'Transferência solicitada — a IA não está mais conduzindo, só registrou o pedido.';
    default:
      // Classe 2 — presença institucional (responde formal e curto, sem conduzir).
      return 'Transferência solicitada — a IA só responde de forma breve até você assumir.';
  }
}

function getBannerText(
  subState: BannerSubState,
  lastDeclinedPayload: EscalationDeclinedOrTimeoutPayload | null,
  queueHeldReason: string | null,
): { primary: string; secondary: string } {
  switch (subState) {
    case 'conducting':
      return {
        primary: 'IA está conduzindo este atendimento',
        secondary:
          'Modo H_c — turnos autônomos. Você acompanha em tempo real e pode retomar a qualquer momento.',
      };
    case 'awaiting-consent':
      return {
        primary: 'IA sugeriu transferência',
        secondary: 'Aguardando confirmação do cliente.',
      };
    case 'confirmed-handoff':
      return {
        primary: 'Cliente confirmou transferência',
        secondary: 'Conversa retornando para você...',
      };
    case 'declined-or-timeout': {
      const reason = lastDeclinedPayload?.reason;
      return {
        primary: reason === 'declined' ? 'Cliente recusou transferência' : 'Cliente não respondeu',
        secondary:
          reason === 'declined'
            ? 'IA seguirá conduzindo a conversa.'
            : 'Conversa em fila prioritária.',
      };
    }
    case 'queue-held':
      return {
        primary: 'IA está segurando a fila',
        secondary: queueHeldSecondaryByClass(queueHeldReason),
      };
  }
}

/**
 * Onda 2 (08/Jul/2026) — o sub-estado nascia SEMPRE 'conducting' (estado local
 * alimentado só por WS ao vivo): quem abria a conversa depois do evento via a
 * fila passiva (escalationState='timed_out_passive' persistido) como se a IA
 * estivesse conduzindo normal. O sub-estado inicial agora deriva do persistido;
 * eventos WS ao vivo seguem sobrepondo.
 */
function initialSubState(persisted: string | null | undefined): BannerSubState {
  if (persisted === 'awaiting_consent') return 'awaiting-consent';
  // S7 PR2 — modo recepcionista: a fila está SEGURADA pela IA (S4). Deriva do
  // estado persistido no reload; o WS `AI_CONDUCTING_QUEUE_HELD` cobre o ao-vivo.
  if (persisted === 'awaiting_claim') return 'queue-held';
  // 'timed_out_passive' → fila prioritária (o texto default do sub-estado, com
  // payload null, é exatamente "Cliente não respondeu / fila prioritária").
  // 'declined' NÃO entra aqui: sem payload o texto sairia factualmente errado
  // ("não respondeu") — e o estado verdadeiro pós-recusa é a IA seguir
  // conduzindo ⇒ 'conducting' é o rótulo honesto no reload.
  if (persisted === 'timed_out_passive') return 'declined-or-timeout';
  return 'conducting';
}

export function AiConductingBanner({
  conversationId,
  persistedEscalationState,
  escalationReason,
  delegation,
  assignedToUserId,
}: {
  conversationId: string;
  /** `Conversation.escalationState` do wire (detalhe da conversa). */
  persistedEscalationState?: string | null;
  /**
   * S7 PR2 (§5.3) — `SerializedConversationDetail.escalationReason` do wire.
   * Motivo persistido da escalação; alimenta a copy do sub-estado `queue-held`
   * (a classe de postura sai de `classifyEscalationReason`). `null`/ausente ⇒
   * classe 2 (conservador).
   */
  escalationReason?: string | null;
  /**
   * S7 PR1 (§4.2) — `SerializedConversationDetail.delegation` do wire.
   * `null`/`undefined`/ausente ⇒ sem janela de delegação por etapa ativa (o
   * banner cai para a máquina de 4 sub-estados de escalation de sempre).
   */
  delegation?: ConversationDelegation | null;
  /**
   * N2-05 (noturna 2, 05/Set/2026 — cenários D2/D9) — `Conversation.assignedToUserId`
   * do wire. Em `queue-held` com conversa ÓRFÃ (`null`), o «Assumir» do
   * cabeçalho faz o CLAIM (mesmo hook do `ClaimBanner`), não só o resume — vide
   * `handleTakeOver`. `undefined` (chamador antigo) ⇒ comportamento anterior.
   */
  assignedToUserId?: string | null;
}): JSX.Element | null {
  const { mutate, isPending: isResuming } = useTakeOver(conversationId);
  const { claim, isPending: isClaiming } = useClaimConversation(conversationId);
  const isPending = isResuming || isClaiming;
  const [subState, setSubState] = useState<BannerSubState>(() =>
    initialSubState(persistedEscalationState),
  );
  const [lastDeclinedPayload, setLastDeclinedPayload] =
    useState<EscalationDeclinedOrTimeoutPayload | null>(null);
  // S7 PR2 — motivo trazido por um evento WS `queue-held` ao vivo (sobrepõe o
  // prop persistido para a copy). `null` ⇒ usa `escalationReason` do detail.
  const [wsQueueHeldReason, setWsQueueHeldReason] = useState<string | null>(null);
  const ttlTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hidden, setHidden] = useState(false);

  // Clear TTL timer on unmount
  useEffect(() => {
    return () => {
      if (ttlTimerRef.current !== null) clearTimeout(ttlTimerRef.current);
    };
  }, []);

  const handleEscalationEvent = useCallback((event: EscalationEvent) => {
    // Clear any pending TTL timer when new event arrives
    if (ttlTimerRef.current !== null) {
      clearTimeout(ttlTimerRef.current);
      ttlTimerRef.current = null;
    }
    setHidden(false);

    if (event.type === 'suggested') {
      setSubState('awaiting-consent');
    } else if (event.type === 'confirmed') {
      setSubState('confirmed-handoff');
      // Auto-hide after TTL (ConversationView will re-render with H_a mode)
      ttlTimerRef.current = setTimeout(() => {
        setHidden(true);
        ttlTimerRef.current = null;
      }, SUBSTATE_CONFIG['confirmed-handoff'].ttlMs ?? 3000);
    } else if (event.type === 'queue-held') {
      // S7 PR2 — a IA passou a segurar a fila ao vivo (modo recepcionista S4).
      setWsQueueHeldReason(event.payload.reason);
      setSubState('queue-held');
    } else {
      // declined-or-timeout
      setLastDeclinedPayload(event.payload);
      setSubState('declined-or-timeout');
    }
  }, []);

  useEscalationEvents(conversationId, handleEscalationEvent);

  const isDelegating = delegation !== null && delegation !== undefined;
  // S7 PR2 — no modo recepcionista o claim "Assume" a conversa (não "Retoma").
  const isQueueHeld = subState === 'queue-held';

  /**
   * N2-05 (noturna 2, 05/Set/2026 — cenários D2/D9). Medido ao vivo 2× no
   * tenant demo: a conversa ÓRFÃ em H_c com handoff pendente (`queue-held`)
   * mostrava «Assumir conversa (⌘R)» aqui, mas o handler só fazia o RESUME
   * (H_c→H_a, audit `ai.conducting.resumed_by_human`) e `assignedToUserId`
   * ficava NULL — a IA parava e ninguém era dono (limbo em H_a). Só depois o
   * `ClaimBanner` aparecia e aí sim atribuía. Dois «Assumir» na mesma tela,
   * só um assumia.
   *
   * Em `queue-held` + órfã o gesto certo é o CLAIM (`useClaimConversation`,
   * o mesmo hook do `ClaimBanner`): o engine, ao assumir uma fila
   * `awaiting_claim`, atribui E flipa H_c→H_a na mesma chamada
   * (`ConversationsService.flipHeldQueueModeOnClaim`, veredito Q-L1a «flip no
   * claim») — órfã em H_a nunca chega a existir. Um resume à parte, depois do
   * claim, receberia 409 («modo atual ≠ H_c») e viraria toast de erro; por isso
   * NÃO se encadeia. Se o flip falhar no engine (é try/catch com warning e o
   * claim fica), o detail volta atribuído e ainda em H_c: este mesmo botão cai
   * então no ramo de resume e conserta no clique seguinte.
   *
   * Conversa já atribuída (a mim ou a outro) ou fora de `queue-held` ⇒ o
   * comportamento de sempre (só resume).
   */
  const handleTakeOver = useCallback(async (): Promise<void> => {
    if (isQueueHeld && assignedToUserId === null) {
      await claim();
      return;
    }
    await mutate();
  }, [isQueueHeld, assignedToUserId, claim, mutate]);

  // ⌘R / ⌘⇧R keyboard shortcut — MESMA ação (`useTakeOver`). Nit Reviewer A
  // #3 (S7 PR1 fix): ⌘⇧R só é reconhecido no sub-estado `delegating` (é o
  // atalho "Reassumir agora" do mockup 2A, §4.2) — fora dele, ⌘⇧R é deixado
  // passar (não faz `preventDefault`/não dispara `mutate`) para não
  // sequestrar o hard-reload do navegador nos outros 4 sub-estados de
  // escalation, onde só ⌘R (sem Shift) sempre existiu.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'r') return;
      if (e.shiftKey && !isDelegating) return;
      e.preventDefault();
      if (!isPending) {
        void handleTakeOver();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleTakeOver, isPending, isDelegating]);

  const config = useMemo(() => SUBSTATE_CONFIG[subState], [subState]);
  const tone = useMemo(() => VARIANT_TOKENS[config.variant], [config.variant]);
  // S7 PR2 — reason efetivo do queue-held: WS ao vivo sobrepõe o prop persistido.
  const effectiveQueueHeldReason = wsQueueHeldReason ?? escalationReason ?? null;
  const text = useMemo(
    () => getBannerText(subState, lastDeclinedPayload, effectiveQueueHeldReason),
    [subState, lastDeclinedPayload, effectiveQueueHeldReason],
  );
  const retakeLabel = isQueueHeld
    ? isPending
      ? 'Assumindo…'
      : 'Assumir'
    : isPending
      ? 'Retomando…'
      : 'Retomar';

  // S7 PR1 (§4.2) — `delegating` deriva do PROP (não da máquina local de
  // eventos): sempre que `delegation` está presente o banner mostra o
  // sub-estado de delegação, independente do `hidden`/`subState` internos
  // (prioridade sobre os 4 sub-estados de escalation). Reage a live
  // re-render (invalidação de query no open/close da janela).
  const remainingLabel = useCountdownLabel(delegation?.timeoutAt ?? null);

  if (delegation !== null && delegation !== undefined) {
    const objectives = delegation.objectives ?? [];
    return (
      <div
        className="composer--hc-locked flex items-center justify-between gap-4 border-t px-6 py-4"
        style={{
          borderColor: VARIANT_TOKENS.neutral.border,
          background: VARIANT_TOKENS.neutral.bg,
          color: VARIANT_TOKENS.neutral.fg,
        }}
        role="status"
        aria-live="polite"
        data-testid="ai-conducting-banner"
        data-substate="delegating"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl" aria-hidden="true">
            ✨
          </span>
          <div className="flex flex-col">
            <span className="text-sm font-semibold" data-testid="delegating-summary">
              {objectives.length > 0
                ? `Você delegou ${objectives.length.toString()} objetivo${objectives.length === 1 ? '' : 's'} à IA`
                : 'Você delegou todas as etapas completáveis à IA'}
            </span>
            {objectives.length > 0 && (
              <span
                className="text-xs"
                style={{ opacity: 0.8 }}
                data-testid="delegating-objectives"
              >
                {objectives.join(' · ')}
              </span>
            )}
            {remainingLabel !== null && (
              <span className="text-xs" style={{ opacity: 0.8 }} data-testid="delegating-countdown">
                Devolve em {remainingLabel} (timeout de segurança)
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            void mutate();
          }}
          disabled={isPending}
          className="btn btn--ai btn--sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="ai-conducting-take-over-button"
          aria-label="Reassumir conversa (Cmd+Shift+R)"
        >
          {isPending ? 'Reassumindo…' : 'Reassumir agora'}
          <kbd
            className="rounded px-1.5 py-0.5 text-xs font-mono"
            style={{ border: '1px solid currentColor', opacity: 0.7 }}
          >
            ⌘⇧R
          </kbd>
        </button>
      </div>
    );
  }

  if (hidden) return null;

  return (
    <div
      className="composer--hc-locked flex items-center justify-between gap-4 border-t px-6 py-4"
      style={{ borderColor: tone.border, background: tone.bg, color: tone.fg }}
      role="status"
      aria-live="polite"
      data-testid="ai-conducting-banner"
      data-substate={subState}
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl" aria-hidden="true">
          ✨
        </span>
        <div className="flex flex-col">
          <span className="text-sm font-semibold">{text.primary}</span>
          <span className="text-xs" style={{ opacity: 0.8 }}>
            {text.secondary}
          </span>
        </div>
      </div>
      {config.showRetakeBtn && (
        <button
          type="button"
          onClick={() => {
            void handleTakeOver();
          }}
          disabled={isPending}
          className="btn btn--ai btn--sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="ai-conducting-take-over-button"
          aria-label={isQueueHeld ? 'Assumir conversa (Cmd+R)' : 'Retomar conversa (Cmd+R)'}
        >
          {retakeLabel}
          <kbd
            className="rounded px-1.5 py-0.5 text-xs font-mono"
            style={{ border: '1px solid currentColor', opacity: 0.7 }}
          >
            ⌘R
          </kbd>
        </button>
      )}
    </div>
  );
}

/**
 * S7 PR1 — rótulo `mm:ss` do tempo restante até `timeoutAt` (re-tick a cada
 * segundo via `setInterval`). `null` quando `timeoutAt` é `null` (sem
 * timeout explícito — nunca ocorre na prática, vide `DELEGATION_DEFAULT_
 * TIMEOUT_MIN`, mas o wire permite e o componente não assume) ou já
 * expirou (o servidor fecha a janela; o valor congela em "00:00" até a
 * invalidação de query chegar).
 */
function useCountdownLabel(timeoutAt: string | null): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (timeoutAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timeoutAt]);

  if (timeoutAt === null) return null;
  const remainingMs = Math.max(0, new Date(timeoutAt).getTime() - now);
  const totalSeconds = Math.floor(remainingMs / 1000);
  const mm = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const ss = (totalSeconds % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}
